import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { IARDashboardDataSliceState } from "../../utils/ARDashboard.types";
import { fetchARDashboardAging, fetchARDashboardCustomersDelay, fetchARDashboardCustomersDue, fetchARDashboardCustomersListDue, fetchARDashboardDSOTrend, fetchARDashboardInvoices, fetchARDashboardTagInvoices, fetchARDashboardPaymentMode, fetchARDashboardNumbers, fetchARDashboardSales, fetchARDashboardListCustomersDelay } from "./ARDashboardThunk";

/**
 * AR Dashboard slice initial state.
 */
const initialState: IARDashboardDataSliceState = {
    numbers: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardNumbers: {
            totalAP: 0,
            totalAPDSO: 0,
            totalAR: 0,
            totalARDSO: 0,
            totalCustomers: 0,
            totalVendors: 0
        }
    },
    sales: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardSales: {
            series: null,
            salesValue: 0,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    invoices: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardInvoices: {
            series: null,
            invoiceValue: 0,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    tagInvoices: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardTagInvoices: {
            series: [],
            rows: [],
            total: 0,
            totalValue: 0
        }
    },
    paymentMode: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardPaymentMode: {
            series: [],
            rows: [],
            total: 0,
            totalValue: 0
        }
    },
    aging: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardAging: {
            agingValue: 0,
            series: null,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    DSOTrend: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardDSOTrend: {
            DSOTrendValue: 0,
            series: null,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    customerDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerDelay: {
            list: []
        },
    },
    customerDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerDue: {
            list: []
        }
    },
    customerAPListDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDue: {
            list: []
        }
    },
    customerARListDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDue: {
            list: []
        }
    },
    customerAPListDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDelay: {
            list: []
        }
    },
    customerARListDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDelay: {
            list: []
        }
    }
}

/**
 * AR Dashboard Slice.
 */
const ARDashboardSlice = createSlice({
    name: 'ARDashboard',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch AR Dashboard Numbers
            .addCase(fetchARDashboardNumbers.pending, (state, action) => {
                state.numbers.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardNumbers.fulfilled, (state, action) => {
                state.numbers.status = ApiStatus.SUCCESS;
                state.numbers.dashboardNumbers = action.payload || [];
                state.numbers.errorMessage = '';
            })
            .addCase(fetchARDashboardNumbers.rejected, (state, action) => {
                state.numbers.status = ApiStatus.FAILED;
                state.numbers.errorMessage = action.error.message
            })


            // Fetch AR Dashboard Sales
            .addCase(fetchARDashboardSales.pending, (state, action) => {
                state.sales.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardSales.fulfilled, (state, action) => {
                state.sales.status = ApiStatus.SUCCESS;
                state.sales.dashboardSales = action.payload || [];
                state.sales.errorMessage = '';
            })
            .addCase(fetchARDashboardSales.rejected, (state, action) => {
                state.sales.status = ApiStatus.FAILED;
                state.sales.errorMessage = action.error.message
            })

            // Fetch AR Dashboard Invoices
            .addCase(fetchARDashboardInvoices.pending, (state, action) => {
                state.invoices.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardInvoices.fulfilled, (state, action) => {
                state.invoices.status = ApiStatus.SUCCESS;
                state.invoices.dashboardInvoices = action.payload || [];
                state.invoices.errorMessage = '';
            })
            .addCase(fetchARDashboardInvoices.rejected, (state, action) => {
                state.invoices.status = ApiStatus.FAILED;
                state.invoices.errorMessage = action.error.message
            })

            // Fetch AR Dashboard Tag-wise Invoices
            .addCase(fetchARDashboardTagInvoices.pending, (state, action) => {
                if (state.tagInvoices) state.tagInvoices.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardTagInvoices.fulfilled, (state, action) => {
                state.tagInvoices = {
                    status: ApiStatus.SUCCESS,
                    dashboardTagInvoices: action.payload,
                    errorMessage: ''
                };
            })
            .addCase(fetchARDashboardTagInvoices.rejected, (state, action) => {
                if (state.tagInvoices) {
                    state.tagInvoices.status = ApiStatus.FAILED;
                    state.tagInvoices.errorMessage = action.error.message;
                }
            })

            // Fetch AR Dashboard Payments by Mode
            .addCase(fetchARDashboardPaymentMode.pending, (state, action) => {
                if (state.paymentMode) state.paymentMode.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardPaymentMode.fulfilled, (state, action) => {
                state.paymentMode = {
                    status: ApiStatus.SUCCESS,
                    dashboardPaymentMode: action.payload,
                    errorMessage: ''
                };
            })
            .addCase(fetchARDashboardPaymentMode.rejected, (state, action) => {
                if (state.paymentMode) {
                    state.paymentMode.status = ApiStatus.FAILED;
                    state.paymentMode.errorMessage = action.error.message;
                }
            })

            // Fetch AR Dashboard Aging
            .addCase(fetchARDashboardAging.pending, (state, action) => {
                state.aging.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardAging.fulfilled, (state, action) => {
                state.aging.status = ApiStatus.SUCCESS;
                state.aging.dashboardAging = action.payload || [];
                state.aging.errorMessage = '';
            })
            .addCase(fetchARDashboardAging.rejected, (state, action) => {
                state.aging.status = ApiStatus.FAILED;
                state.aging.errorMessage = action.error.message
            })

            // Fetch AR Dashboard DSO-Trend
            .addCase(fetchARDashboardDSOTrend.pending, (state, action) => {
                state.DSOTrend.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardDSOTrend.fulfilled, (state, action) => {
                state.DSOTrend.status = ApiStatus.SUCCESS;
                state.DSOTrend.dashboardDSOTrend = action.payload || [];
                state.DSOTrend.errorMessage = '';
            })
            .addCase(fetchARDashboardDSOTrend.rejected, (state, action) => {
                state.DSOTrend.status = ApiStatus.FAILED;
                state.DSOTrend.errorMessage = action.error.message
            })

            // Fetch AR Dashboard Due
            .addCase(fetchARDashboardCustomersDue.pending, (state, action) => {
                state.customerDue.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardCustomersDue.fulfilled, (state, action) => {
                state.customerDue.status = ApiStatus.SUCCESS;
                state.customerDue.dashboardCustomerDue = action.payload || [];
                state.customerDue.errorMessage = '';
            })
            .addCase(fetchARDashboardCustomersDue.rejected, (state, action) => {
                state.customerDue.status = ApiStatus.FAILED;
                state.customerDue.errorMessage = action.error.message
            })
            
            // Fetch AP Dashboard customer list Due
            .addCase(fetchARDashboardCustomersListDue.pending, (state, action) => {
                state.customerARListDue.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardCustomersListDue.fulfilled, (state, action) => {
                state.customerARListDue.status = ApiStatus.SUCCESS;
                state.customerARListDue.dashboardCustomerListDue = action.payload || [];
                state.customerARListDue.errorMessage = '';
            })
            .addCase(fetchARDashboardCustomersListDue.rejected, (state, action) => {
                state.customerARListDue.status = ApiStatus.FAILED;
                state.customerARListDue.errorMessage = action.error.message
            })

            // Fetch AR Dashboard Delay
            .addCase(fetchARDashboardCustomersDelay.pending, (state, action) => {
                state.customerDelay.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardCustomersDelay.fulfilled, (state, action) => {
                state.customerDelay.status = ApiStatus.SUCCESS;
                state.customerDelay.dashboardCustomerDelay = action.payload || [];
                state.customerDelay.errorMessage = '';
            })
            .addCase(fetchARDashboardCustomersDelay.rejected, (state, action) => {
                state.customerDelay.status = ApiStatus.FAILED;
                state.customerDelay.errorMessage = action.error.message
            })

             // Fetch AR Dashboard customer list Delay
             .addCase(fetchARDashboardListCustomersDelay.pending, (state, action) => {
                state.customerARListDelay.status = ApiStatus.LOADING;
            })
            .addCase(fetchARDashboardListCustomersDelay.fulfilled, (state, action) => {
                state.customerARListDelay.status = ApiStatus.SUCCESS;
                state.customerARListDelay.dashboardCustomerListDelay = action.payload || [];
                state.customerARListDelay.errorMessage = '';
            })
            .addCase(fetchARDashboardListCustomersDelay.rejected, (state, action) => {
                state.customerARListDelay.status = ApiStatus.FAILED;
                state.customerARListDelay.errorMessage = action.error.message
            })
    }
});

export default ARDashboardSlice.reducer;
